import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  User, Mail, Shield, LogOut, Star, Edit3,
  Swords, Lock, Eye, EyeOff, CheckCircle, AlertCircle,
  Snowflake, Link2, ShoppingBag, Gift, BarChart2,
  Clock, Sword, Coins, ScrollText, Trophy, Crown,
  Download, Vote, ShoppingCart, ChevronRight, Calendar, Ticket,
} from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import BuyCrystalsModal from '../components/BuyCrystalsModal';
import SEOMeta from '../components/SEOMeta';
import AltLinksTab from '../components/cuenta/AltLinksTab';
import { validateGamePassword, PASSWORD_MAX_LEN, PASSWORD_HINT } from '../lib/passwordRules';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? `${window.location.protocol}//${window.location.hostname}:8001`;

// ── Quality colors (compartido) ───────────────────────────────────────────────
const QUALITY_COLOR = {
  legendary: '#FF8000',
  epic:       '#A335EE',
  rare:       '#0070DD',
  uncommon:   '#1EFF00',
  common:     '#FFFFFF',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const CLASS_COLORS = {
  1:'#C69B3A',2:'#F58CBA',3:'#ABD473',4:'#FFF569',5:'#FFFFFF',
  6:'#C41F3B',7:'#0070DE',8:'#69CCF0',9:'#9482C9',11:'#FF7D0A',
};
const CLASS_NAMES = {
  1:'Guerrero',2:'Paladín',3:'Cazador',4:'Pícaro',5:'Sacerdote',
  6:'Caballero de la Muerte',7:'Chamán',8:'Mago',9:'Brujo',11:'Druida',
};
const RACE_NAMES = {
  1:'Humano',2:'Orco',3:'Enano',4:'Elfo de la Noche',5:'No-Muerto',
  6:'Tauren',7:'Gnomo',8:'Troll',10:'Elfo de Sangre',11:'Draenei',
};

// Bonos de la warband (users.vouchers): servicios que se canjean en la tienda.
const VOUCHER_LABELS = {
  pase_nivel_80:      'Subida a 80',
  maestria_profesion: 'Maestría en 2 profesiones',
  paquete_vuelo:      'Vuelo Ártico',
  cambio_faccion:     'Cambio de Facción',
  caja_fuerte_mercenario: 'Alijo del Mercenario',
};
const voucherText = (v) => Object.entries(v || {}).filter(([, n]) => n > 0)
  .map(([k, n]) => `${n} × ${VOUCHER_LABELS[k] ?? k}`).join(' · ');

function formatPlayed(secs) {
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ══════════════════════════════════════════════════════════════════════════════
// PERFIL · Dashboard "Centro de Mando" (A+)
// ══════════════════════════════════════════════════════════════════════════════
function PerfilDashboard({ user, onGoTab, onEmailChange }) {
  const [totals, setTotals]           = useState(null);
  const [charCount, setCharCount]     = useState(null);
  const [lastPurchase, setLastPurchase] = useState(undefined); // undefined=load · null=ninguna
  const [referral, setReferral]       = useState(null);
  const [spark, setSpark]             = useState([]);
  const [showRecarga, setShowRecarga] = useState(false);
  const [copied, setCopied]           = useState(false);
  const [emailEdit, setEmailEdit]     = useState(false);
  const [emailForm, setEmailForm]     = useState({ newEmail: '', password: '' });
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [resendStatus, setResendStatus] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);

  const crystals = user?.frost_crystals ?? 0;

  const handleResendVerification = async () => {
    setResendLoading(true); setResendStatus(null);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/resend-verification`, {}, { withCredentials: true });
      setResendStatus({ ok: true, msg: 'Correo de verificación reenviado.' });
    } catch (err) {
      setResendStatus({ ok: false, msg: err.response?.data?.detail || 'No se pudo reenviar el correo.' });
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/account/characters/stats`, { withCredentials: true })
      .then(({ data }) => {
        setCharCount(data.length);
        setTotals(data.reduce((acc, c) => ({
          played:       acc.played       + c.played_secs,
          hk:           acc.hk           + c.honorable_kills,
          quests:       acc.quests       + c.quests_done,
          achievements: acc.achievements + c.achievements_done,
          gold:         acc.gold         + c.gold,
        }), { played: 0, hk: 0, quests: 0, achievements: 0, gold: 0 }));
      })
      .catch(() => { setCharCount(0); setTotals(null); });

    axios.get(`${BACKEND_URL}/api/account/purchases`, { withCredentials: true })
      .then(({ data }) => setLastPurchase(Array.isArray(data) && data.length ? data[0] : null))
      .catch(() => setLastPurchase(null));

    axios.get(`${BACKEND_URL}/api/account/referral`, { withCredentials: true })
      .then(({ data }) => setReferral(data))
      .catch(() => {});

    axios.get(`${BACKEND_URL}/api/account/transactions`, { withCredentials: true })
      .then(({ data }) => setSpark((Array.isArray(data) ? data : []).slice(0, 8).map(t => t.crystals || 0).reverse()))
      .catch(() => {});
  }, []);

  const referralLink = referral
    ? `${window.location.origin}/registro?ref=${referral.referral_code}`
    : '';
  const copyLink = () => {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const maxSpark = Math.max(...spark, 1);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setEmailLoading(true);
    setEmailStatus(null);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/account/email`,
        { new_email: emailForm.newEmail.trim(), current_password: emailForm.password },
        { withCredentials: true }
      );
      onEmailChange && onEmailChange(data.email);
      setEmailEdit(false);
      setEmailForm({ newEmail: '', password: '' });
      setEmailStatus({ ok: true, msg: 'Correo actualizado correctamente.' });
      setTimeout(() => setEmailStatus(null), 3000);
    } catch (err) {
      setEmailStatus({ ok: false, msg: err.response?.data?.detail || 'Error al cambiar el correo' });
    } finally {
      setEmailLoading(false);
    }
  };

  const STATS = [
    { icon: <Swords size={18} />, color: '#69CCF0', v: charCount ?? '—', k: 'Personajes' },
    { icon: <Clock size={18} />,  color: '#00e5ff', v: totals ? formatPlayed(totals.played) : '—', k: 'Tiempo jugado' },
    { icon: <Sword size={18} />,  color: '#f87171', v: totals ? totals.hk.toLocaleString() : '—', k: 'Kills PvP' },
    { icon: <Trophy size={18} />, color: '#a78bfa', v: totals ? totals.achievements.toLocaleString() : '—', k: 'Logros' },
  ];

  const lpColor = lastPurchase ? (QUALITY_COLOR[lastPurchase.product_quality] ?? QUALITY_COLOR.common) : '#fff';
  const lpDate  = lastPurchase?.purchased_at
    ? new Date(lastPurchase.purchased_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
    : '';

  return (
    <>
      {/* Tarjeta destacada de Cristales (con Votar dentro) */}
      <div className="cd-crystal">
        <div>
          <div className="cd-eyebrow" style={{ marginBottom: 8 }}>Cristales de Escarcha</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 9 }}>
            <Snowflake size={26} style={{ color: '#00e5ff' }} />
            <span className="cd-crystal-num">{crystals.toLocaleString()}</span>
          </div>
          <div className="cd-crystal-sub">100 cristales = 1€ · se usan en la tienda del servidor</div>
          {voucherText(user?.vouchers) && (
            <a href="/tienda" className="cd-crystal-sub"
              style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, color: '#fcd34d', textDecoration: 'none' }}>
              <Ticket size={13} /> Bonos sin canjear: {voucherText(user.vouchers)} · canjéalos en la tienda →
            </a>
          )}
        </div>
        <div className="cd-crystal-right">
          {spark.length > 1 && (
            <div className="cd-spark" title="Recargas recientes">
              {spark.map((v, i) => (
                <span key={i} style={{ height: 6 + (v / maxSpark) * 36 }} />
              ))}
            </div>
          )}
          <div className="cd-crystal-btns">
            <button className="cd-btn cd-btn--cyan" onClick={() => setShowRecarga(true)}>
              + Recargar
            </button>
            <a className="cd-btn cd-btn--gold" href="/votar">
              <Vote size={13} /> Votar&nbsp;<span style={{ color: '#4ade80', fontWeight: 700 }}>+50</span>
            </a>
          </div>
        </div>
      </div>

      {/* Tira de estadísticas */}
      <div className="cd-stats">
        {STATS.map((s) => (
          <div key={s.k} className="cd-stat">
            <div className="cd-stat-ico" style={{ color: s.color }}>{s.icon}</div>
            <div className="cd-stat-v">{s.v}</div>
            <div className="cd-stat-k">{s.k}</div>
          </div>
        ))}
      </div>

      {/* Dos columnas */}
      <div className="cd-cols">
        {/* Datos de la cuenta */}
        <div className="cd-panel">
          <div className="cd-eyebrow">Datos de la cuenta</div>
          <div className="cd-info-list">
            <div className="cd-info-row">
              <span className="cd-info-ico"><User size={15} /></span>
              <span className="cd-info-label">Usuario</span>
              <span className="cd-info-val" data-testid="cuenta-info-username">{user?.username}</span>
            </div>

            {/* Correo con form inline */}
            <div className="cd-info-row">
              <span className="cd-info-ico"><Mail size={15} /></span>
              <span className="cd-info-label">Correo</span>
              <span className="cd-info-val" data-testid="cuenta-info-email">{user?.email}</span>
              <span className="cd-info-link" onClick={() => { setEmailEdit(v => !v); setEmailStatus(null); }}>
                {emailEdit ? 'Cancelar' : <>Cambiar <ChevronRight size={13} /></>}
              </span>
            </div>
            {!user?.email_verified && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#fbbf24', paddingLeft: 26, flexWrap: 'wrap' }}>
                <AlertCircle size={12} /> Correo sin verificar — no cuenta para recompensas de referidos.
                <span className="cd-info-link" style={{ fontSize: 11 }} onClick={handleResendVerification}>
                  {resendLoading ? 'Enviando…' : 'Reenviar verificación'}
                </span>
              </div>
            )}
            {resendStatus && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: resendStatus.ok ? '#4ade80' : '#f87171', paddingLeft: 26 }}>
                {resendStatus.ok ? <CheckCircle size={11} /> : <AlertCircle size={11} />} {resendStatus.msg}
              </div>
            )}
            {emailEdit && (
              <form onSubmit={handleEmailSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingLeft: 26, paddingBottom: 4 }}>
                <input
                  type="email" required autoFocus
                  className="form-input"
                  style={{ fontSize: 12, padding: '7px 10px' }}
                  placeholder="Nuevo correo electrónico"
                  value={emailForm.newEmail}
                  onChange={e => setEmailForm(f => ({ ...f, newEmail: e.target.value }))}
                />
                <input
                  type="password" required
                  className="form-input"
                  style={{ fontSize: 12, padding: '7px 10px' }}
                  placeholder="Contraseña actual (para confirmar)"
                  value={emailForm.password}
                  onChange={e => setEmailForm(f => ({ ...f, password: e.target.value }))}
                />
                {emailStatus && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: emailStatus.ok ? '#4ade80' : '#f87171' }}>
                    {emailStatus.ok ? <CheckCircle size={11} /> : <AlertCircle size={11} />}
                    {emailStatus.msg}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="submit" className="cd-btn cd-btn--cyan" style={{ padding: '6px 14px', fontSize: 11 }} disabled={emailLoading}>
                    {emailLoading ? 'Guardando…' : 'Guardar'}
                  </button>
                  <button type="button" className="cd-btn cd-btn--ghost" style={{ padding: '6px 14px', fontSize: 11 }} onClick={() => { setEmailEdit(false); setEmailStatus(null); }}>
                    Cancelar
                  </button>
                </div>
              </form>
            )}
            {!emailEdit && emailStatus?.ok && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#4ade80', paddingLeft: 26 }}>
                <CheckCircle size={11} /> {emailStatus.msg}
              </div>
            )}

            <div className="cd-info-row">
              <span className="cd-info-ico"><Lock size={15} /></span>
              <span className="cd-info-label">Seguridad</span>
              <span className="cd-info-val">Contraseña</span>
              <span className="cd-info-link" onClick={() => onGoTab('seguridad')}>Cambiar <ChevronRight size={13} /></span>
            </div>
            {user?.created_at && (
              <div className="cd-info-row">
                <span className="cd-info-ico"><Calendar size={15} /></span>
                <span className="cd-info-label">Registro</span>
                <span className="cd-info-val" style={{ color: 'rgba(255,255,255,0.55)' }}>{user.created_at}</span>
              </div>
            )}
            {user?.last_login && (
              <div className="cd-info-row">
                <span className="cd-info-ico"><Clock size={15} /></span>
                <span className="cd-info-label">Último login</span>
                <span className="cd-info-val" style={{ color: 'rgba(255,255,255,0.55)' }}>{user.last_login}</span>
              </div>
            )}
          </div>
        </div>

        {/* Accesos rápidos + última compra */}
        <div className="cd-col">
          <div className="cd-eyebrow">Accesos rápidos</div>
          <a href="/tienda" className="cd-quick">
            <div className="cd-quick-ico"><ShoppingCart size={18} /></div>
            <div className="cd-quick-body">
              <div className="cd-quick-t">Tienda del servidor</div>
              <div className="cd-quick-s">Gasta tus cristales</div>
            </div>
            <span className="cd-quick-arrow"><ChevronRight size={16} /></span>
          </a>
          <a href="/descargas" className="cd-quick">
            <div className="cd-quick-ico"><Download size={18} /></div>
            <div className="cd-quick-body">
              <div className="cd-quick-t">Descargas</div>
              <div className="cd-quick-s">Cliente y addons</div>
            </div>
            <span className="cd-quick-arrow"><ChevronRight size={16} /></span>
          </a>

          {/* Última compra */}
          <div className="cd-panel" style={{ padding: '14px 16px' }}>
            <div className="cd-last-head">
              <span className="cd-eyebrow">Última compra</span>
              <span className="cd-info-link" onClick={() => onGoTab('compras')}>Ver historial <ChevronRight size={13} /></span>
            </div>
            {lastPurchase === undefined ? (
              <p className="cd-last-empty">Cargando…</p>
            ) : lastPurchase === null ? (
              <p className="cd-last-empty">Aún no has comprado nada. <a href="/tienda" style={{ color: '#00e5ff' }}>Ir a la tienda →</a></p>
            ) : (
              <div className="cd-last-row">
                {lastPurchase.product_icon ? (
                  <img
                    src={`https://wow.zamimg.com/images/wow/icons/medium/${lastPurchase.product_icon}.jpg`}
                    alt=""
                    className="cd-last-icon"
                    style={{ border: `1px solid ${lpColor}55` }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="cd-last-icon cd-last-icon--fallback" style={{ border: `1px solid ${lpColor}55` }}>
                    <ShoppingBag size={16} style={{ color: lpColor }} />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="cd-last-name" style={{ color: lpColor }}>{lastPurchase.product_name}</div>
                  <div className="cd-last-meta">{lastPurchase.character_name || 'Bonos de cuenta'}{lpDate ? ` · ${lpDate}` : ''}</div>
                </div>
                <div className="cd-last-price">
                  {lastPurchase.voucher_used
                    ? <><Ticket size={12} /><span>bono</span></>
                    : <><Snowflake size={12} /><span>{lastPurchase.price_crystals?.toLocaleString()}</span></>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Referidos */}
      {referral && (
        <div className="cd-panel cd-referral">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Gift size={15} style={{ color: '#00e5ff' }} />
            <span className="cd-eyebrow">Invita amigos</span>
          </div>
          <div className="cd-ref-grid">
            <div className="cd-ref-left">
              <p style={{ fontSize: 12.5, color: 'rgba(224,247,255,.5)', margin: '0 0 10px' }}>
                Gana <b style={{ color: '#4ade80' }}>+200</b> cristales cuando tu amigo verifique su correo y su
                personaje alcance <b style={{ color: '#4ade80' }}>nivel 20</b>. Él recibe <b style={{ color: '#4ade80' }}>+100</b>.
              </p>
              <div className="cd-ref-copy">
                <div className="cd-ref-input">{referralLink}</div>
                <button className="cd-btn cd-btn--ghost" onClick={copyLink}>
                  {copied ? '✓ Copiado' : 'Copiar'}
                </button>
              </div>
            </div>
            <div className="cd-ref-stats">
              <div className="cd-ref-stat">
                <div className="cd-ref-stat-v" style={{ color: '#00e5ff' }}>{referral.invited_count}</div>
                <div className="cd-ref-stat-k">AMIGOS</div>
              </div>
              <div className="cd-ref-sep" />
              <div className="cd-ref-stat">
                <div className="cd-ref-stat-v" style={{ color: '#4ade80' }}>{referral.crystals_earned?.toLocaleString()}</div>
                <div className="cd-ref-stat-k">GANADOS</div>
              </div>
              {referral.pending_count > 0 && (
                <>
                  <div className="cd-ref-sep" />
                  <div className="cd-ref-stat">
                    <div className="cd-ref-stat-v" style={{ color: '#fbbf24' }}>{referral.pending_count}</div>
                    <div className="cd-ref-stat-k">PENDIENTES</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <BuyCrystalsModal
        open={showRecarga}
        onClose={() => setShowRecarga(false)}
        user={user}
        crystals={crystals}
      />
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CRISTALES tab
// ══════════════════════════════════════════════════════════════════════════════
function CristalesTab({ user }) {
  const [transactions, setTransactions] = useState([]);
  const [loadingTx, setLoadingTx]       = useState(true);
  const [showRecarga, setShowRecarga]   = useState(false);
  const [crystals]                      = useState(user?.frost_crystals ?? 0);
  const [referral, setReferral]         = useState(null);
  const [copied, setCopied]             = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/account/transactions`, { withCredentials: true })
      .then(({ data }) => setTransactions(data))
      .catch(() => setTransactions([]))
      .finally(() => setLoadingTx(false));
    axios.get(`${BACKEND_URL}/api/account/referral`, { withCredentials: true })
      .then(({ data }) => setReferral(data))
      .catch(() => {});
  }, []);

  const referralLink = referral
    ? `${window.location.origin}/registro?ref=${referral.referral_code}`
    : '';

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{
        background: 'rgba(0,229,255,0.05)', border: '1px solid rgba(0,229,255,0.2)',
        borderRadius: 12, padding: '20px 24px', textAlign: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 4 }}>
          <Snowflake size={18} style={{ color: '#00e5ff' }} />
          <span style={{ color: '#e0f7ff', fontWeight: 800, fontSize: 22 }}>{crystals.toLocaleString()}</span>
          <span style={{ color: 'rgba(0,229,255,0.6)', fontSize: 13 }}>Cristales de Escarcha</span>
        </div>
        <p style={{ color: 'rgba(224,247,255,0.35)', fontSize: 11, margin: '0 0 14px' }}>
          100 Cristales = 1€ · Se usan en la tienda del servidor
        </p>
        <button
          onClick={() => setShowRecarga(true)}
          style={{
            background: 'linear-gradient(135deg, #0077b6, #00b4d8)',
            border: 'none', borderRadius: 8,
            color: '#fff', fontWeight: 700, fontSize: 12,
            letterSpacing: 1.5, padding: '9px 24px', cursor: 'pointer',
          }}
        >
          + RECARGAR CRISTALES
        </button>
      </div>

      {referral && (
        <div style={{
          background: 'rgba(0,229,255,0.03)', border: '1px solid rgba(0,229,255,0.15)',
          borderRadius: 12, padding: '18px 20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Gift size={15} style={{ color: '#00e5ff' }} />
            <span style={{ color: 'rgba(224,247,255,0.7)', fontSize: 11, fontWeight: 700, letterSpacing: 1.5 }}>
              INVITA AMIGOS
            </span>
          </div>
          <p style={{ color: 'rgba(224,247,255,0.45)', fontSize: 12, margin: '0 0 12px' }}>
            Gana <span style={{ color: '#4ade80', fontWeight: 700 }}>+200 Cristales</span> cuando tu amigo verifique su
            correo y su personaje alcance <span style={{ color: '#4ade80', fontWeight: 700 }}>nivel 20</span>.
            Él recibe <span style={{ color: '#4ade80', fontWeight: 700 }}>+100 Cristales</span>.
          </p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <div style={{
              flex: 1, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)',
              borderRadius: 7, padding: '8px 12px',
              color: 'rgba(224,247,255,0.5)', fontSize: 11, fontFamily: 'monospace',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {referralLink}
            </div>
            <button
              onClick={copyLink}
              style={{
                background: copied ? 'rgba(74,222,128,0.15)' : 'rgba(0,229,255,0.1)',
                border: `1px solid ${copied ? 'rgba(74,222,128,0.4)' : 'rgba(0,229,255,0.25)'}`,
                borderRadius: 7, padding: '8px 14px', cursor: 'pointer',
                color: copied ? '#4ade80' : '#00e5ff', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
              }}
            >
              {copied ? '✓ COPIADO' : 'COPIAR'}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#00e5ff', fontWeight: 800, fontSize: 20 }}>{referral.invited_count}</div>
              <div style={{ color: 'rgba(224,247,255,0.35)', fontSize: 10, letterSpacing: 1 }}>AMIGOS</div>
            </div>
            <div style={{ width: 1, background: 'rgba(0,229,255,0.1)' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#4ade80', fontWeight: 800, fontSize: 20 }}>{referral.crystals_earned.toLocaleString()}</div>
              <div style={{ color: 'rgba(224,247,255,0.35)', fontSize: 10, letterSpacing: 1 }}>CRISTALES GANADOS</div>
            </div>
            {referral.pending_count > 0 && (
              <>
                <div style={{ width: 1, background: 'rgba(0,229,255,0.1)' }} />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#fbbf24', fontWeight: 800, fontSize: 20 }}>{referral.pending_count}</div>
                  <div style={{ color: 'rgba(224,247,255,0.35)', fontSize: 10, letterSpacing: 1 }}>PENDIENTES</div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div>
        <p style={{ color: 'rgba(224,247,255,0.4)', fontSize: 11, letterSpacing: 1.5, marginBottom: 10, fontWeight: 700 }}>
          HISTORIAL DE RECARGAS
        </p>
        {loadingTx ? (
          <p style={{ color: 'rgba(0,229,255,0.4)', fontSize: 12 }}>Cargando...</p>
        ) : transactions.length === 0 ? (
          <p style={{ color: 'rgba(224,247,255,0.25)', fontSize: 12 }}>No hay recargas registradas.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {transactions.map((tx) => {
              const date = tx.created_at
                ? new Date(tx.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
                : '—';
              return (
                <div key={tx._id || tx.created_at} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: 'rgba(0,229,255,0.03)', border: '1px solid rgba(0,229,255,0.1)',
                  borderRadius: 8, padding: '10px 14px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Snowflake size={13} style={{ color: '#00e5ff' }} />
                    <span style={{ color: '#00e5ff', fontWeight: 700, fontSize: 13 }}>
                      +{tx.crystals?.toLocaleString()} cristales
                    </span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: 'rgba(224,247,255,0.6)', fontSize: 12 }}>
                      {tx.amount_eur?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                    </div>
                    <div style={{ color: 'rgba(224,247,255,0.3)', fontSize: 10 }}>{date}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BuyCrystalsModal open={showRecarga} onClose={() => setShowRecarga(false)} user={user} crystals={crystals} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPRAS tab
// ══════════════════════════════════════════════════════════════════════════════
function ComprasTab() {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/account/purchases`, { withCredentials: true })
      .then(({ data }) => setPurchases(data))
      .catch(() => setPurchases([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="armory-loading" style={{ fontSize: '0.82rem' }}>Cargando historial...</p>;
  if (!purchases.length) return (
    <div className="compras-empty">
      <ShoppingBag size={32} style={{ opacity: 0.25, marginBottom: 10 }} />
      <p>No has realizado ninguna compra aún.</p>
      <a href="/tienda" className="btn-primary" style={{ marginTop: 10, padding: '8px 20px', fontSize: 12 }}>Ir a la tienda</a>
    </div>
  );

  return (
    <div className="compras-list">
      {purchases.map((p) => {
        const color = QUALITY_COLOR[p.product_quality] ?? QUALITY_COLOR.common;
        const date  = p.purchased_at ? new Date(p.purchased_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
        return (
          <div key={p._id || `${p.product_name}-${p.purchased_at}`} className="compra-row">
            <div className="compra-icon-wrap" style={{ borderColor: `${color}55` }}>
              {p.product_icon
                ? <img src={`https://wow.zamimg.com/images/wow/icons/medium/${p.product_icon}.jpg`} alt="" width={32} height={32} style={{ borderRadius: 4 }} onError={e => { e.target.style.display = 'none'; }} />
                : <ShoppingBag size={16} style={{ color }} />
              }
            </div>
            <div className="compra-info">
              <span className="compra-name" style={{ color }}>{p.product_name}</span>
              <span className="compra-meta">{p.character_name || 'Bonos de cuenta'} · {date}</span>
            </div>
            <div className="compra-price">
              {p.voucher_used ? (
                <><Ticket size={11} style={{ color: '#fcd34d' }} /><span>bono</span></>
              ) : (
                <><Snowflake size={11} style={{ color: '#00e5ff' }} /><span>{p.price_crystals.toLocaleString()}</span></>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// WARBAND tab  (fusión de Personajes + Estadísticas)
// ══════════════════════════════════════════════════════════════════════════════
function WarbandTab() {
  const [chars, setChars]   = useState([]);
  const [extras, setExtras] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND_URL}/api/account/characters/stats`, { withCredentials: true }),
      axios.get(`${BACKEND_URL}/api/account/characters`,       { withCredentials: true }),
    ])
      .then(([statsRes, charsRes]) => {
        const sorted = [...statsRes.data].sort((a, b) => b.played_secs - a.played_secs);
        setChars(sorted);
        const map = {};
        for (const c of charsRes.data) map[c.name] = c;
        setExtras(map);
      })
      .catch(() => setError('No se pudieron cargar los personajes.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="cuenta-tab-empty">Cargando warband...</p>;
  if (error)   return <p className="cuenta-tab-empty">{error}</p>;
  if (!chars.length) return <p className="cuenta-tab-empty">No tienes personajes creados aún.</p>;

  const totals = chars.reduce((acc, c) => ({
    played:       acc.played       + c.played_secs,
    hk:           acc.hk           + c.honorable_kills,
    quests:       acc.quests       + c.quests_done,
    achievements: acc.achievements + c.achievements_done,
    gold:         acc.gold         + c.gold,
  }), { played: 0, hk: 0, quests: 0, achievements: 0, gold: 0 });

  const statCard = (icon, label, value, color = '#00e5ff') => (
    <div style={{
      flex: 1, minWidth: 90, textAlign: 'center',
      background: 'rgba(0,0,0,0.2)', borderRadius: 10, padding: '14px 10px',
      border: '1px solid rgba(255,255,255,0.05)',
    }}>
      <div style={{ color, marginBottom: 4 }}>{icon}</div>
      <div style={{ color: '#fff', fontWeight: 800, fontSize: 18 }}>{value}</div>
      <div style={{ color: 'rgba(224,247,255,0.35)', fontSize: 10, letterSpacing: 1, marginTop: 2 }}>{label}</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Resumen global */}
      <div style={{
        background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.15)',
        borderRadius: 12, padding: '16px 20px',
      }}>
        <p style={{ color: 'rgba(224,247,255,0.5)', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, margin: '0 0 14px' }}>
          RESUMEN DE WARBAND
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {statCard(<Clock size={16} />,      'TIEMPO JUGADO', formatPlayed(totals.played),             '#00e5ff')}
          {statCard(<Sword size={16} />,      'KILLS PVP',     totals.hk.toLocaleString(),              '#f87171')}
          {statCard(<ScrollText size={16} />, 'QUESTS',        totals.quests.toLocaleString(),          '#fbbf24')}
          {statCard(<Trophy size={16} />,     'LOGROS',        totals.achievements.toLocaleString(),    '#a78bfa')}
          {statCard(<Coins size={16} />,      'ORO TOTAL',     `${totals.gold.toLocaleString()}g`,      '#fcd34d')}
        </div>
      </div>

      {/* Lista de personajes */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {chars.map((c, i) => {
          const isTop  = i === 0;
          const ext    = extras[c.name] || {};
          const color  = ext.class_color || CLASS_COLORS[c.class] || '#aaa';
          const border = isTop ? '#FF8000' : `${color}33`;
          const bg     = isTop ? 'rgba(255,128,0,0.05)' : 'rgba(0,0,0,0.25)';
          const raceName  = ext.race      || RACE_NAMES[c.race]   || `Raza ${c.race}`;
          const className = ext.class_name || CLASS_NAMES[c.class] || `Clase ${c.class}`;

          return (
            <Link
              key={c.guid}
              to={`/armeria/${encodeURIComponent(c.name)}`}
              style={{ display: 'block', background: bg, border: `1px solid ${border}`, borderRadius: 12, padding: '14px 18px', textDecoration: 'none', transition: 'border-color .18s, background .18s' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = isTop ? '#FF8000cc' : `${color}88`; e.currentTarget.style.background = isTop ? 'rgba(255,128,0,0.09)' : 'rgba(0,229,255,0.04)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = border; e.currentTarget.style.background = bg; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                {isTop
                  ? <Crown size={14} style={{ color: '#FF8000', flexShrink: 0 }} />
                  : <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                }
                <span style={{ color, fontWeight: 700, fontSize: 15 }}>{c.name}</span>
                {ext.online && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#4ade80' }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
                    En línea
                  </span>
                )}
                <span style={{ color: 'rgba(224,247,255,0.35)', fontSize: 12 }}>
                  Nv.{c.level} · {raceName} · {className}
                  {ext.guild && <span style={{ color: 'rgba(224,247,255,0.45)' }}> · &lt;{ext.guild}&gt;</span>}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { icon: <Clock size={13} />,      label: 'Jugado',    val: formatPlayed(c.played_secs),          col: '#00e5ff' },
                  { icon: <Sword size={13} />,      label: 'Kills PvP', val: c.honorable_kills.toLocaleString(),   col: '#f87171' },
                  { icon: <ScrollText size={13} />, label: 'Quests',    val: c.quests_done.toLocaleString(),       col: '#fbbf24' },
                  { icon: <Trophy size={13} />,     label: 'Logros',    val: c.achievements_done.toLocaleString(), col: '#a78bfa' },
                  { icon: <Coins size={13} />,      label: 'Oro',       val: `${c.gold.toLocaleString()}g ${c.silver}s`, col: '#fcd34d' },
                ].map(({ icon, label, val, col }) => (
                  <div key={label} style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    background: 'rgba(255,255,255,0.04)', borderRadius: 8,
                    padding: '6px 12px', border: '1px solid rgba(255,255,255,0.06)',
                  }}>
                    <span style={{ color: col }}>{icon}</span>
                    <span style={{ color: 'rgba(224,247,255,0.4)', fontSize: 11 }}>{label}:</span>
                    <span style={{ color: '#e0e8f0', fontWeight: 600, fontSize: 12 }}>{val}</span>
                  </div>
                ))}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SEGURIDAD tab
// ══════════════════════════════════════════════════════════════════════════════
function SeguridadTab() {
  const [form, setForm]       = useState({ current: '', nuevo: '', confirmar: '' });
  const [show, setShow]       = useState({ current: false, nuevo: false, confirmar: false });
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(false);

  const toggleShow = (field) => setShow((s) => ({ ...s, [field]: !s[field] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    if (form.nuevo !== form.confirmar) {
      setStatus({ ok: false, msg: 'Las contraseñas nuevas no coinciden' });
      return;
    }
    const passwordError = validateGamePassword(form.nuevo);
    if (passwordError) {
      setStatus({ ok: false, msg: passwordError });
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/account/password`,
        { current_password: form.current, new_password: form.nuevo },
        { withCredentials: true }
      );
      setStatus({ ok: true, msg: 'Contraseña actualizada. Tu sesión de juego se ha cerrado.' });
      setForm({ current: '', nuevo: '', confirmar: '' });
    } catch (err) {
      setStatus({ ok: false, msg: err.response?.data?.detail || 'Error al cambiar la contraseña' });
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { key: 'current',   label: 'Contraseña actual',    placeholder: '••••••••' },
    { key: 'nuevo',     label: 'Nueva contraseña',     placeholder: PASSWORD_HINT, newPassword: true },
    { key: 'confirmar', label: 'Confirmar contraseña', placeholder: '••••••••', newPassword: true },
  ];

  return (
    <form className="cuenta-pass-form" onSubmit={handleSubmit}>
      {fields.map(({ key, label, placeholder, newPassword }) => (
        <div key={key} className="cuenta-pass-field">
          <label className="cuenta-pass-label">{label}</label>
          <div className="cuenta-pass-input-wrap">
            <input
              type={show[key] ? 'text' : 'password'}
              className="form-input"
              placeholder={placeholder}
              value={form[key]}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
              maxLength={newPassword ? PASSWORD_MAX_LEN : undefined}
              required
            />
            <button type="button" className="cuenta-pass-eye" onClick={() => toggleShow(key)}>
              {show[key] ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
        </div>
      ))}

      {status && (
        <div className={`cuenta-pass-feedback ${status.ok ? 'cuenta-pass-feedback--ok' : 'cuenta-pass-feedback--err'}`}>
          {status.ok ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
          {status.msg}
        </div>
      )}

      <button type="submit" className="btn-primary cuenta-pass-submit" disabled={loading}>
        {loading ? 'Guardando...' : 'Cambiar contraseña'}
      </button>
    </form>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Config
// ══════════════════════════════════════════════════════════════════════════════
const ROLE_CONFIG = {
  admin:  { label: 'Administrador', color: '#FFD700', bg: 'rgba(255,215,0,0.12)', border: 'rgba(255,215,0,0.4)' },
  player: { label: 'Jugador',       color: '#00e5ff', bg: 'rgba(0,229,255,0.10)', border: 'rgba(0,229,255,0.35)' },
};

// NOTA: la pestaña "Dev" se ha movido a /admin (panel de administración).
const TABS = [
  { id: 'perfil',    label: 'Perfil',    icon: User },
  { id: 'warband',   label: 'Warband',   icon: Swords },
  { id: 'cristales', label: 'Cristales', icon: Snowflake },
  { id: 'compras',   label: 'Compras',   icon: ShoppingBag },
  { id: 'alters',    label: 'Alters',    icon: Link2 },
  { id: 'seguridad', label: 'Seguridad', icon: Lock },
];

// ══════════════════════════════════════════════════════════════════════════════
// Main component
// ══════════════════════════════════════════════════════════════════════════════
export default function CuentaPage() {
  const navigate                = useNavigate();
  const [user, setUser]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('perfil');
  const [surveyStatus, setSurveyStatus] = useState(null);
  const [altLinkRequests, setAltLinkRequests] = useState([]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => navigate('/login'))
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${BACKEND_URL}/api/survey/status`, { withCredentials: true })
      .then(({ data }) => setSurveyStatus(data))
      .catch(() => setSurveyStatus(null));
    // Solicitudes de ligado de cuentas pendientes de responder (banner + contador)
    axios.get(`${BACKEND_URL}/api/account/alt-links`, { withCredentials: true })
      .then(({ data }) => setAltLinkRequests(data.received.map((r) => r.account)))
      .catch(() => setAltLinkRequests([]));
  }, [user]);

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/');
      window.location.reload();
    } finally {
      setLogoutLoading(false);
    }
  };

  const roleConfig = user ? (ROLE_CONFIG[user.role] || ROLE_CONFIG.player) : ROLE_CONFIG.player;

  if (loading) {
    return (
      <PageWrapper scrollable>
        <Navbar />
        <main className="register-main">
          <div className="armory-loading">Cargando cuenta...</div>
        </main>
        <Footer />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper scrollable>
      <SEOMeta title="Mi cuenta" noIndex />
      <Navbar />
      <main className="register-main anim-fade-up" style={{ animationDelay: '0.2s', paddingTop: '2rem', paddingBottom: '3rem' }}>
        <div className="cuenta-card" data-testid="cuenta-card">

          {/* Cabecera de identidad (siempre visible) */}
          <div className="cd-head">
            <div className="cd-id">
              <div
                className="cuenta-avatar"
                style={{ borderColor: roleConfig.color, boxShadow: `0 0 22px ${roleConfig.color}33` }}
                data-testid="cuenta-avatar"
              >
                {user?.username?.[0]?.toUpperCase() || '?'}
              </div>
              <div>
                <h1 className="cuenta-username" data-testid="cuenta-username">{user?.username}</h1>
                <span
                  className="cuenta-role-badge"
                  style={{ background: roleConfig.bg, border: `1px solid ${roleConfig.border}`, color: roleConfig.color }}
                  data-testid="cuenta-role"
                >
                  {user?.role === 'admin' ? <Shield size={11} /> : <Star size={11} />}
                  {roleConfig.label}
                </span>
              </div>
            </div>
            <div className="cd-head-actions">
              {user?.role === 'admin' && (
                <a href="/admin" className="cd-btn cd-btn--admin" data-testid="go-to-admin">
                  <Edit3 size={14} /> Panel de Admin
                </a>
              )}
              <button className="cd-btn cd-btn--logout" onClick={handleLogout} disabled={logoutLoading} data-testid="logout-btn">
                <LogOut size={14} /> {logoutLoading ? 'Cerrando...' : 'Cerrar Sesión'}
              </button>
            </div>
          </div>

          {surveyStatus && !surveyStatus.submitted && (
            <Link
              to="/encuesta"
              data-testid="survey-banner"
              style={{
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'space-between',
                gap:            12,
                marginTop:      18,
                padding:        '12px 14px',
                borderRadius:   10,
                border:         '1px solid rgba(34, 211, 238, 0.35)',
                background:     'rgba(34, 211, 238, 0.08)',
                color:          '#e0f7ff',
                textDecoration: 'none',
                fontSize:       14,
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Gift size={18} style={{ color: '#22d3ee' }} />
                Completa la encuesta opcional (4 preguntas) y consigue
                {' '}<strong style={{ color: '#22d3ee' }}>100 Cristales de Escarcha</strong>.
              </span>
              <ChevronRight size={16} />
            </Link>
          )}

          {altLinkRequests.length > 0 && activeTab !== 'alters' && (
            <button
              type="button"
              onClick={() => setActiveTab('alters')}
              data-testid="altlinks-banner"
              style={{
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'space-between',
                gap:            12,
                width:          '100%',
                marginTop:      18,
                padding:        '12px 14px',
                borderRadius:   10,
                border:         '1px solid rgba(250, 204, 21, 0.4)',
                background:     'rgba(250, 204, 21, 0.08)',
                color:          '#e0f7ff',
                fontSize:       14,
                textAlign:      'left',
                cursor:         'pointer',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Link2 size={18} style={{ color: '#facc15' }} />
                {altLinkRequests.length === 1
                  ? <>La cuenta <strong style={{ color: '#facc15' }}>«{altLinkRequests[0]}»</strong> quiere ligarse contigo para usar alters como bots.</>
                  : <>Tienes <strong style={{ color: '#facc15' }}>{altLinkRequests.length} solicitudes</strong> para ligar cuentas y usar alters como bots.</>}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#facc15', fontWeight: 600, whiteSpace: 'nowrap' }}>
                Revisar <ChevronRight size={16} />
              </span>
            </button>
          )}

          <div className="cuenta-divider" style={{ margin: '22px 0 18px' }} />

          {/* Tabs */}
          <div className="cuenta-tabs">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                className={`cuenta-tab ${activeTab === id ? 'cuenta-tab--active' : ''}`}
                onClick={() => setActiveTab(id)}
              >
                <Icon size={13} />
                {label}
                {id === 'alters' && altLinkRequests.length > 0 && (
                  <span style={{ marginLeft: 6, minWidth: 16, padding: '0 5px', borderRadius: 8, background: '#facc15', color: '#05101f', fontSize: 10, fontWeight: 800, lineHeight: '16px' }}>
                    {altLinkRequests.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="cuenta-tab-content" style={{ marginTop: 20 }}>
            {activeTab === 'perfil' && (
              <PerfilDashboard
                user={user}
                onGoTab={setActiveTab}
                onEmailChange={(newEmail) => setUser(u => ({ ...u, email: newEmail }))}
              />
            )}
            {activeTab === 'warband'   && <WarbandTab />}
            {activeTab === 'cristales' && <CristalesTab user={user} />}
            {activeTab === 'compras'      && <ComprasTab />}
            {activeTab === 'alters'       && (
              <AltLinksTab onReceivedChange={setAltLinkRequests} />
            )}
            {activeTab === 'seguridad'    && <SeguridadTab />}
          </div>
        </div>
      </main>
      <Footer />
    </PageWrapper>
  );
}
